import { generateClient, get, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import GROUPS, { GROUPS_CAN_ADD_USER } from "constants/cognito_groups";
import { createUSER, updateUSER, updateUserLastLogin, updateUserLastActive } from "graphql/user";

const API = generateClient();

/**
 * Update user's last login timestamp
 * @param {string} userId - The user's ID
 * @param {string} email - The user's email
 */
export async function updateLastLogin(userId, email) {
  try {
    console.log('[updateLastLogin] Starting update for:', { userId, email });

    if (!userId || !email) {
      console.warn('[updateLastLogin] Cannot update last login: missing userId or email', { userId, email });
      return null;
    }

    const now = new Date().toISOString();
    console.log('[updateLastLogin] Calling GraphQL mutation with:', { userId, lastLoginAt: now });

    const response = await API.graphql({
      query: updateUserLastLogin,
      variables: {
        id: userId,
        lastLoginAt: now
      }
    });

    console.log(`[updateLastLogin] Last login updated for user ${email}:`, now);
    console.log('[updateLastLogin] Full response:', JSON.stringify(response, null, 2));
    return response?.data?.updateUSER;
  } catch (error) {
    console.error('[updateLastLogin] Error updating last login:', error);
    console.error('[updateLastLogin] Error details:', JSON.stringify(error, null, 2));
    // Don't throw error to prevent login from failing if lastLogin update fails
    return null;
  }
}

/**
 * Update user's last active timestamp
 * @param {string} userId - The user's ID
 * @param {string} email - The user's email
 */
export async function updateLastActive(userId, email) {
  try {
    console.log('[updateLastActive] Starting update for:', { userId, email });

    if (!userId || !email) {
      console.warn('[updateLastActive] Cannot update last active: missing userId or email', { userId, email });
      return null;
    }

    const now = new Date().toISOString();
    console.log('[updateLastActive] Calling GraphQL mutation with:', { userId, lastActiveAt: now });

    const response = await API.graphql({
      query: updateUserLastActive,
      variables: {
        id: userId,
        lastActiveAt: now
      }
    });

    console.log(`[updateLastActive] Last active updated for user ${email}:`, now);
    console.log('[updateLastActive] Full response:', JSON.stringify(response, null, 2));
    return response?.data?.updateUSER;
  } catch (error) {
    console.error('[updateLastActive] Error updating last active:', error);
    console.error('[updateLastActive] Error details:', JSON.stringify(error, null, 2));
    // Don't throw error to prevent app from failing if lastActive update fails
    return null;
  }
}

export async function adminDisableUser(user) {
  await post({
    apiName: "AdminQueries", // Using exact API name from aws-exports.js
    path: "/adminDisableUser",
    options: {
      body: {
        "username" : user.email
      },
      headers: {
        'Content-Type' : 'application/json',
        Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
      }
    }
  });
}

export async function adminDeleteUser(user) {
  await post({
    apiName: "AdminQueries",
    path: "/adminDeleteUser",
    options: {
      body: {
        "username" : user.email
      },
      headers: {
        'Content-Type' : 'application/json',
        Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
      }
    }
  });
}

export async function enableUser(user) {
  await post({
    apiName: "AdminQueries",
    path: "/enableUser",
    options: {
      body: {
        "username" : user.email
      },
      headers: {
        'Content-Type' : 'application/json',
        Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
      }
    }
  });
}

export async function signUserUp(user, group = GROUPS.ClientAdmin, organizationID, success) {
  const { email } = user
  try {
    const response = await post({
      apiName: "AdminQueries",
      path: "/inviteUser", 
      options: {
        body: {
          "username": email
        },
        headers: {
          'Content-Type': 'application/json',
          Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
        }
      }
    }).response;
    const inviteUserResponse = await response.body.json();
    console.log("user invited", inviteUserResponse);
    
    await saveUserCognitoGroups(email, group);
    await addUserToDB({...user, organizationID}, success);
    
  } catch (error) {
    if (error?.response?.data?.message === "An account with the given email already exists.") {
      await saveUserCognitoGroups(email, group);
      await addUserToDB({...user, organizationID}, success);
    } else {
      throw error;
    }
  }
}

export function saveUserCognitoGroups(email, group) {
  const canAddUser = GROUPS_CAN_ADD_USER.includes(group)
  addUserToGroup(email, group, () => (canAddUser && addUserToGroup(email, GROUPS.AddUser)))
}

async function addUserToGroup(username, group, success) {
  try {
    const { body } = await post({
      apiName: "AdminQueries",
      path: "/addUserToGroup",
      options: {
        body: {
          "username": username,
          "groupname": group
        },
        headers: {
          'Content-Type': 'application/json',
          Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
        }
      }
    }).response;
    const data = await body.json();
    
    if (success) {
      success(data);
    }
  } catch (error) {
    console.log("err", error);
  }
}

export async function getGroupsForUser (email) {
  const { body } = await get({
    apiName: "AdminQueries",
    path: "/listGroupsForUser?username=" + encodeURIComponent(email),
    options: {
      headers: {
        'Content-Type' : 'application/json',
        Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
      }
    }
  }).response;
  const groups = await body.json()
  return groups?.Groups?.map(group => group.GroupName)?.filter(group => group !== GROUPS.AddUser)
    || []
}

export async function removeUserFromGroups(email, groups=[], shouldRemoveFromAddUser=true) {
  let isInAddUser = false
  for(let i=0; i<groups.length; i++) {
    !isInAddUser && (isInAddUser = GROUPS_CAN_ADD_USER.includes(groups[i]))
    await removeUserFromGroup(email, groups[i])
  }

  if(shouldRemoveFromAddUser && isInAddUser) {
    await removeUserFromGroup(email, GROUPS.AddUser)
  }
}

async function removeUserFromGroup(email, group) {
  await post({
    apiName: "AdminQueries",
    path: "/removeUserFromGroup",
    options: {
      body: {
        "username" : email,
        "groupname": group
      },
      headers: {
        'Content-Type' : 'application/json',
        Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
      }
    }
  });
}

async function getCognitoUser(username, success) {
  try {
    const {body} = await get({
      apiName: "AdminQueries",
      path: "/getUser" + `?username=${username}`,
      options: {
        headers: {
          'Content-Type': 'application/json',
          Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
        }
      }
    }).response;

    const data = await body.json();
    
    if (success) {
      success(data);
    }
  } catch (error) {
    console.log("err", error);
  }
}

export async function listCognitoUsers(limit = 60, nextToken = null) {
  try {
    let path = `/listUsers?limit=${limit}`;
    if (nextToken) {
      path += `&token=${encodeURIComponent(nextToken)}`;
    }
    
    const { body } = await get({
      apiName: "AdminQueries",
      path: path,
      options: {
        headers: {
          'Content-Type': 'application/json',
          Authorization: (await fetchAuthSession()).tokens.accessToken.toString()
        }
      }
    }).response;
    
    const data = await body.json();
    return data;
  } catch (error) {
    console.error('Error fetching Cognito users:', error);
    return { Users: [] };
  }
}

async function addUserToDB(user, success) {
  try {
    const isCreate = !user.id;
    const result = await API.graphql({
      query: isCreate ? createUSER : updateUSER,
      variables: {
        input: {
          ...user,
          status: "ACTIVE"
        },
        ...(isCreate ? {} : { email: user.email })
      }
    });
    
    if (success) {
      success(result?.data?.[isCreate ? "createUSER" : "updateUSER"]);
    }
  } catch (error) {
    console.log("error", error);
  }
}
